import axios from "axios";
import { BASE_API_URL } from "../config";
import {
  ACCESS_TOKEN_KEY,
  getLocalStorageItem,
  REFRESH_TOKEN_KEY,
  setLocalStorageItem,
} from "../constant/localStorage";
import { ROUTES } from "../constant/routes";

/**
 * Class-based HTTP client with automatic token refresh functionality
 */
class HttpClient {
  constructor(baseURL = BASE_API_URL) {
    this.baseURL = baseURL;
    this.isRefreshing = false;
    this.failedQueue = [];
    this.maxRetries = 0;
    this.retryDelay = 1000;

    this.axiosInstance = this.createAxiosInstance();
    this.setupInterceptors();
  }

  /**
   * Creates and configures the axios instance
   */
  createAxiosInstance() {
    return axios.create({
      baseURL: this.baseURL,
      headers: {
        "Content-Type": "application/json",
      },
    });
  }

  /**
   * Sets up request and response interceptors
   */
  setupInterceptors() {
    this.setupRequestInterceptor();
    this.setupResponseInterceptor();
  }

  /**
   * Request interceptor to attach access token (skip for public endpoints)
   */
  setupRequestInterceptor() {
    this.axiosInstance.interceptors.request.use(
      (config) => {
        // Skip token attachment for public endpoints
        const token = this.getAccessToken();
        if (token) {
          config.headers.Authorization = `Bearer ${token}`;
        }

        return config;
      },
      (error) => Promise.reject(error),
    );
  }

  /**
   * Response interceptor for auto-refresh logic
   */
  setupResponseInterceptor() {
    this.axiosInstance.interceptors.response.use(
      (response) => response.data,
      async (error) => {
        const originalRequest = error.config;
        // Handle 401 errors with token refresh (skip for public endpoints)
        if (this.shouldRefreshToken(error)) {
          return this.handleTokenRefresh(originalRequest);
        }

        // Handle network errors with retry
        // if (this.shouldRetryRequest(error, originalRequest)) {
        //   return this.retryRequest(originalRequest);
        // }

        return Promise.reject(error?.response?.data || error);
      },
    );
  }

  /**
   * Checks if token should be refreshed
   */
  shouldRefreshToken(error) {
    const isUnauthorized = error.response?.status === 401;
    const refreshToken = this.getRefreshToken();

    if (isUnauthorized && refreshToken) {
      return true;
    }

    if (isUnauthorized && !refreshToken) {
      this.handleRefreshFailure(); // no refresh token → handle failure
    }
    return false;
  }
  /**
   * Checks if request should be retried
   */
  shouldRetryRequest(error, originalRequest) {
    return (
      !error.response &&
      (!originalRequest._retryCount ||
        originalRequest._retryCount < this.maxRetries)
    );
  }

  /**
   * Handles token refresh logic
   */
  async handleTokenRefresh(originalRequest) {
    if (this.isRefreshing) {
      return this.queueRequest(originalRequest);
    }

    originalRequest._retry = true;
    this.isRefreshing = true;

    try {
      const newToken = await this.refreshAccessToken();
      this.processQueue(null, newToken);
      return this.retryWithNewToken(originalRequest, newToken);
    } catch (error) {
      this.processQueue(error, null);
      this.handleRefreshFailure();
      throw error;
    } finally {
      this.isRefreshing = false;
    }
  }

  /**
   * Queues failed requests while token is being refreshed
   */
  queueRequest(originalRequest) {
    return new Promise((resolve, reject) => {
      this.failedQueue.push({ resolve, reject });
    })
      .then((token) => this.retryWithNewToken(originalRequest, token))
      .catch(Promise.reject);
  }

  /**
   * Retries request with new token
   */
  retryWithNewToken(originalRequest, token) {
    originalRequest.headers.Authorization = `Bearer ${token}`;
    return this.axiosInstance(originalRequest);
  }

  /**
   * Refreshes the access token
   */
  async refreshAccessToken() {
    const refresh_token = this.getRefreshToken();

    if (!refresh_token) {
      throw new Error("No refresh token available");
    }

    const response = await axios.post(`${this.baseURL}refresh-token`, {
      refresh_token,
    });

    const { token: accessToken } = response?.data?.data || {};
    this.setAccessToken(accessToken);

    return accessToken;
  }

  /**
   * Retries request with exponential backoff
   */
  async retryRequest(originalRequest) {
    const retryCount = originalRequest._retryCount || 0;
    originalRequest._retryCount = retryCount + 1;

    const delay = this.retryDelay * Math.pow(2, retryCount);
    await this.sleep(delay);

    return this.axiosInstance(originalRequest);
  }

  /**
   * Processes the queue of failed requests
   */
  processQueue(error, token = null) {
    this.failedQueue.forEach((prom) => {
      if (error) {
        prom.reject(error);
      } else {
        prom.resolve(token);
      }
    });

    this.failedQueue = [];
  }

  /**
   * Handles refresh token failure
   */
  handleRefreshFailure() {
    this.clearTokens();
    this.redirectToLogin();
  }

  /**
   * Token management methods
   */
  getAccessToken() {
    return getLocalStorageItem(ACCESS_TOKEN_KEY);
  }

  getRefreshToken() {
    return getLocalStorageItem(REFRESH_TOKEN_KEY);
  }

  setAccessToken(token) {
    setLocalStorageItem(ACCESS_TOKEN_KEY, token);
    this.axiosInstance.defaults.headers.common.Authorization = `Bearer ${token}`;
  }

  setRefreshToken(token) {
    setLocalStorageItem(REFRESH_TOKEN_KEY, token);
  }

  setTokens(accessToken, refreshToken) {
    this.setAccessToken(accessToken);
    this.setRefreshToken(refreshToken);
  }

  clearTokens() {
    localStorage.removeItem(ACCESS_TOKEN_KEY);
    localStorage.removeItem(REFRESH_TOKEN_KEY);
    delete this.axiosInstance.defaults.headers.common.Authorization;
  }

  /**
   * Redirects to login page
   */
  redirectToLogin() {
    if (typeof window !== "undefined") {
      window.location.href = ROUTES.SIGNIN;
    }
  }

  /**
   * Utility method for delays
   */
  sleep(ms) {
    return new Promise((resolve) => setTimeout(resolve, ms));
  }

  /**
   * Public API methods for authenticated requests
   */
  get(url, config = {}) {
    return this.axiosInstance.get(url, config);
  }

  post(url, data, config = {}) {
    return this.axiosInstance.post(url, data, config);
  }

  put(url, data, config = {}) {
    return this.axiosInstance.put(url, data, config);
  }

  patch(url, data, config = {}) {
    return this.axiosInstance.patch(url, data, config);
  }

  delete(url, config = {}) {
    return this.axiosInstance.delete(url, config);
  }

  /**
   * Public API methods (no auth required)
   */

  async request({ method, url, data = null, config = {} }) {
    try {
      const response = await axios({
        method,
        url: `${this.baseURL}${url}`,
        data,
        ...config,
      });
      return response?.data;
    } catch (error) {
      return Promise.reject(error?.response?.data || error);
    }
  }

  publicPost(url, data, config = {}) {
    return this.request({ method: "post", url, data, config });
  }

  publicGet(url, config = {}) {
    return this.request({ method: "get", url, data: null, config });
  }

  /**
   * Get the underlying axios instance
   */
  getInstance() {
    return this.axiosInstance;
  }

  /**
   * Update configuration
   */
  updateConfig(config) {
    Object.assign(this.axiosInstance.defaults, config);
  }

  /**
   * Set base URL
   */
  setBaseURL(baseURL) {
    this.baseURL = baseURL;
    this.axiosInstance.defaults.baseURL = baseURL;
  }

  getValidAccessToken = async () => {
    try {
      const token = this.getAccessToken();
      if (!token) return null;

      const payloadBase64 = token.split(".")[1];
      const decodedPayload = JSON.parse(atob(payloadBase64));

      const currentTime = Math.floor(Date.now() / 1000);

      // ✅ If expired → refresh
      if (!decodedPayload?.exp || decodedPayload?.exp <= currentTime) {
        return await this.refreshAccessToken();
      }

      // ✅ If still valid → return same token
      return token;
    } catch (err) {
      console.error("Invalid token:", err);
      return null;
    }
  };
}

// Create and export singleton instance
const httpClient = new HttpClient();

export default httpClient;
